import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBullhorn, faUsers, faChartLine, faRobot, faVideo, faImage, faCalendarAlt, faRocket } from '@fortawesome/free-solid-svg-icons';
import Header from '../../component/layout/header';
import Footer from '../../component/layout/footer';
import Navigate from '../../../../util/Navigate';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import IndieSubmenu from '../../component/section/publishers/IndieSubmenu';

function PublisherDevlogPage() {
    const currentUrl = typeof window !== 'undefined' ? window.location.href : '';

    return (
        <>
            <Helmet>
                <meta charSet="utf-8" />
                <title>Turn Your Dev Logs Into Marketing Content | Glitch Gaming</title>
                <meta
                    name="description"
                    content="Balance development and marketing effortlessly by turning dev logs into engaging social media content with the Social Scheduler."
                />
                <meta name="robots" content="index, follow" />
                <meta property="og:title" content="Turn Your Dev Logs Into Marketing Content | Glitch"  />
                <meta property="og:description" content="Balance development and marketing effortlessly by turning dev logs into engaging social media content with the Social Scheduler."  />
                <meta property="og:type" content="website" data-rh="true"  />
                <meta property="og:url" content={currentUrl} data-rh="true"  />
                <meta property="og:image" content="https://www.glitch.fun/assets/images/publishers/devlog_banner.webp" data-rh="true"  />
            </Helmet>
            <Header />
            
            {/* Hero Section */}
            <section className="devlog-hero">
                <div className="container">
                    <div className="row align-items-center">
                        <div className="col-lg-6 text-center text-lg-start">
                            <h1 className="display-4 fw-bold mb-4 pt-5">
                                Turn Dev Logs Into <span className="text-gradient">Marketing Magic</span>
                            </h1>
                            <p className="lead mb-5">Effortlessly grow your audience while focusing on game development with our automated social scheduler.</p>
                            <div className="cta-group">
                                <Link to={Navigate.publishersOnboardingStep1Page()} className="btn btn-primary btn-lg me-3 mb-3">
                                    Get Started <FontAwesomeIcon icon={faRocket} className="ms-2" />
                                </Link>
                                <Link to={Navigate.demoPage()} className="btn btn-outline-light btn-lg mb-3">
                                   Book A Demo
                                </Link>
                            </div>
                        </div>
                        <div className="col-lg-6">
                            <div className="hero-visual mt-5 mt-lg-0 pt-5">
                                <img
                                    src="/assets/images/publishers/devlog_banner.webp"
                                    alt="Dev Log Marketing"
                                    className="img-fluid rounded-3 shadow-lg border border-3 border-white"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Stats Section */}
            <section className="py-5 bg-light text-black">
                <div className="container">
                    <div className="row align-items-center mb-5">
                        <div className="col-md-6 mb-4">
                            <div className="stat-card h-100 p-4 rounded-3 shadow-sm">
                                <h3 className='text-center text-black'>Successful Games Market <u>1-2 Years</u> Before Launch</h3>
                                <div className="image-container" style={{ height: '300px', overflow: 'hidden' }}>
                                    <img
                                        src="/assets/images/publishers/wishlist_years.png"
                                        alt="Wishlist Timeline"
                                        className="img-fluid rounded shadow mt-3 border border-white border-2 w-100 h-100 object-fit-cover"
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="col-md-6 mb-4">
                            <div className="stat-card h-100 p-4 rounded-3 shadow-sm">
                                <h3 className='text-center text-black'>Yet 54% Of Developers Do No Marketing At All</h3>
                                <div className="image-container" style={{ height: '300px', overflow: 'hidden' }}>
                                    <img
                                        src="/assets/images/publishers/game_devs_dont_market.jpg"
                                        alt="Marketing Problem"
                                        className="img-fluid rounded shadow mt-3 border border-white border-2 w-100 h-100 object-fit-cover"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    <div className="row justify-content-center">
                        <div className="col-lg-8 text-center">
                            <div className="alert alert-warning p-4 rounded-3">
                                <h2 className="mb-3 text-black">Don't Make This <u>Fatal</u> Marketing Mistake</h2>
                                <p className="lead">
                                    Most indie games fail because they don't build an audience before launch. Our Social Scheduler makes it effortless to promote your game consistently while you focus on development.
                                </p>
                                <ul className="list-unstyled lead text-start d-inline-block">
                                    <li className="mb-2">
                                        <FontAwesomeIcon icon={faRobot} className="text-primary me-2" />
                                        Drive wishlist sign-ups automatically
                                    </li>
                                    <li className="mb-2">
                                        <FontAwesomeIcon icon={faBullhorn} className="text-success me-2" />
                                        Generate content for all major platforms
                                    </li>
                                    <li className="mb-2">
                                        <FontAwesomeIcon icon={faUsers} className="text-warning me-2" />
                                        Grow your community with minimal effort
                                    </li>
                                </ul>
                                <div className='text-center mt-4'>
                                    <Link className="btn btn-primary btn-lg px-5" to={Navigate.publishersOnboardingStep1Page()}>Start Using the Scheduler</Link>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* How It Works */}
            <section id="how-it-works" className="workflow-steps py-5 text-black">
                <div className="container">
                    <div className="text-center mb-5">
                        <h2 className="display-5 fw-bold mb-3 text-black">Transform Dev Logs Into Social Content</h2>
                        <p className="lead">Just 5 minutes once a week is all it takes</p>
                    </div>
                    
                    <div className="row g-4">
                        <div className="col-md-6 text-center">
                            <div className="step-card bg-white p-4 rounded-4 shadow-sm h-100">
                                <div className="step-number bg-primary text-white rounded-circle mb-3">1</div>
                                <h3 className="h4 mb-3">Submit Your Update</h3>
                                <img 
                                    src="/assets/images/publishers/scheduler_submit.png" 
                                    alt="Submit Update" 
                                    className="img-fluid rounded-3 mb-3 border border-1"
                                    style={{ height: '200px', objectFit: 'contain' }}
                                />
                                <p>Once a week, submit a short update about new features, bug fixes, or progress.</p>
                            </div>
                        </div>
                        <div className="col-md-6 text-center">
                            <div className="step-card bg-white p-4 rounded-4 shadow-sm h-100">
                                <div className="step-number bg-success text-white rounded-circle mb-3">2</div>
                                <h3 className="h4 mb-3">We Create The Content</h3>
                                <img 
                                    src="/assets/images/publishers/scheduler_results.png" 
                                    alt="Content Creation" 
                                    className="img-fluid rounded-3 mb-3 border border-1"
                                    style={{ height: '200px', objectFit: 'contain' }}
                                />
                                <p>We transform your update into engaging posts optimized for each platform.</p>
                            </div>
                        </div>
                    </div>
                    
                    <div className="text-center mt-5">
                        <div className="d-inline-flex align-items-center bg-light p-3 rounded-pill">
                            <img className='img-fluid me-2' style={{ width: '35px' }} src="/assets/images/publishers/clock.png" />
                            <h4 className="mb-0 text-black">5 Minutes Once A Week Is All It Takes!</h4>
                        </div>
                    </div>
                </div>
            </section>

            {/* Platforms Section */}
            <section className="py-5 bg-dark">
                <div className="container">
                    <h2 className="text-center text-white mb-5">Your Content Goes To All Major Platforms</h2>
                    <div className="row justify-content-center">
                        <div className="col-12 d-flex flex-wrap justify-content-center gap-4">
                            <div className="platform-icon">
                                <img src={'/assets/images/creators/youtube.png'} alt="YouTube" />
                                <span>YouTube</span>
                            </div>
                            <div className="platform-icon">
                                <img src="/assets/images/creators/tiktok.png" alt="TikTok" />
                                <span>TikTok</span>
                            </div>
                            <div className="platform-icon">
                                <img src="/assets/images/creators/reddit.png" alt="Reddit" />
                                <span>Reddit</span>
                            </div>
                            <div className="platform-icon">
                                <img src="/assets/images/creators/facebook.png" alt="Facebook" />
                                <span>Facebook</span>
                            </div>
                            <div className="platform-icon">
                                <img src="/assets/images/creators/twitter.png" alt="Twitter" />
                                <span>Twitter</span>
                            </div>
                            <div className="platform-icon">
                                <img src="/assets/images/creators/instagram.png" alt="Instagram" />
                                <span>Instagram</span>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Video Demo */}
            <section className="py-5 bg-light text-black">
                <div className="container text-center">
                    <h2 className="mb-4 text-black">See How It Works</h2>
                    <p className="lead mb-5">Watch our quick demo to see how easy it is to turn dev logs into marketing content</p>
                    <div className="ratio ratio-16x9 border border-2 rounded-3 overflow-hidden shadow-sm mb-5">
                        <iframe
                            src="https://www.youtube.com/embed/7yKPRmbr66Y"
                            allowFullScreen
                            title="Dev Log Promo Video"
                        ></iframe>
                    </div>
                    <Link className="btn btn-primary btn-lg px-5" to={Navigate.publishersOnboardingStep1Page()}>Get Started for Free</Link>
                </div>
            </section>

            {/* Features */}
            <section className="py-5 text-black">
                <div className="container">
                    <h2 className="text-center mb-5">Features Designed for Game Developers</h2>
                    <div className="row g-4">
                        <div className="col-md-4">
                            <div className="feature-card p-4 rounded-3 h-100">
                                <FontAwesomeIcon icon={faCalendarAlt} size="3x" className="text-primary mb-3" />
                                <h3 className='text-black'>Weekly Reminders</h3>
                                <p>Never forget to post with our automated email reminders to keep your dev logs consistent.</p>
                            </div>
                        </div>
                        <div className="col-md-4">
                            <div className="feature-card p-4 rounded-3 h-100">
                                <FontAwesomeIcon icon={faImage} size="3x" className="text-success mb-3" />
                                <h3 className='text-black'>Visual Content</h3>
                                <p>Upload images or videos and we'll optimize them for each social platform's requirements.</p>
                            </div>
                        </div>
                        <div className="col-md-4">
                            <div className="feature-card p-4 rounded-3 h-100">
                                <FontAwesomeIcon icon={faChartLine} size="3x" className="text-warning mb-3" />
                                <h3 className='text-black'>Performance Tracking</h3>
                                <p>See which content performs best and get insights to improve your future updates.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Gamification CTA */}
            <section className="py-5 bg-primary text-white">
                <div className="container text-center">
                    <h2 className="mb-4">Gamified Motivation</h2>
                    <p className="lead mb-5">We've turned social media marketing into a game with weekly goals and achievements to keep you motivated.</p>
                    <img 
                        src="/assets/images/publishers/gamified_social_media.png" 
                        alt="Gamified Dashboard" 
                        className="img-fluid rounded-3 shadow border border-white border-2 mb-5"
                        style={{ maxHeight: '400px' }}
                    />
                    <div>
                        <Link className="btn btn-light btn-lg px-5" to={Navigate.publishersOnboardingStep1Page()}>Start Your Free Trial</Link>
                    </div>
                </div>
            </section>

            {/* Blog CTA */}
            <section className="py-5 bg-dark text-white">
                <div className="container text-center">
                    <h2 className="mb-4">Learn More Marketing Strategies</h2>
                    <p className="lead mb-4">Read our in-depth guide on building in public and using dev logs for game marketing.</p>
                    <Link to={"https://glitch.ghost.io/how-to-makert-your-game-with-devlogs-and-building-in-public/"} target='_blank' className="btn btn-danger btn-lg px-5">Read Full Article</Link>
                </div>
            </section>

            <IndieSubmenu />
            <Footer />

            <style jsx>{`
                .devlog-hero {
                    padding: 100px 0;
                    background: linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.7)), url("/assets/images/publishers/devlog_banner.webp");
                    background-size: cover;
                    background-position: center;
                    color: white;
                }

                .text-gradient {
                    background: linear-gradient(90deg, #ff0052 0%, #9400ff 100%);
                    -webkit-background-clip: text;
                    -webkit-text-fill-color: transparent;
                }

                .hero-visual {
                    position: relative;
                    border-radius: 16px;
                    overflow: hidden;
                    box-shadow: 0 25px 50px -12px rgba(0,0,0,0.25);
                }

                .stat-card {
                    background: white;
                    border: 1px solid rgba(0,0,0,0.1);
                    transition: transform 0.3s ease;
                }

                .stat-card:hover {
                    transform: translateY(-5px);
                }

                .image-container {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                .object-fit-cover {
                    object-fit: cover;
                }

                .workflow-steps {
                    background: #f8f9ff;
                }

                .step-card {
                    transition: transform 0.3s ease;
                    border: 1px solid rgba(0,0,0,0.05);
                }

                .step-card:hover {
                    transform: translateY(-5px);
                }

                .step-number {
                    width: 50px;
                    height: 50px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-weight: bold;
                    font-size: 1.5rem;
                    margin: 0 auto;
                }

                .platform-icon {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    width: 100px;
                }

                .platform-icon img {
                    width: 50px;
                    height: 50px;
                    margin-bottom: 0.5rem;
                }

                .platform-icon span {
                    color: white;
                    font-size: 0.9rem;
                }

                .feature-card {
                    background: white;
                    border: 1px solid rgba(0,0,0,0.05);
                    transition: transform 0.3s ease;
                }

                .feature-card:hover {
                    transform: translateY(-5px);
                    box-shadow: 0 10px 15px -3px rgba(0,0,0,0.1);
                }

                @media (max-width: 768px) {
                    .devlog-hero {
                        padding: 60px 0;
                    }
                    
                    .hero-visual {
                        margin-top: 2rem;
                    }
                }
            `}</style>
        </>
    );
}

export default PublisherDevlogPage;